import { useMutation, useQuery } from '@apollo/client'
import {
  BodyText,
  Button,
  CaptionText,
  Col,
  HeadingText,
  Link,
  Row,
} from '@paypalcorp/pp-react'
import { navigate } from '@reach/router'
import { merge } from 'lodash-es'
import ReadOnlyCard from 'ppbl/components/ReadOnlyCard'
import SpinnerAdapter from 'ppbl/components/SpinnerAdapter'
import { updateApplicationCachedData } from 'ppbl/graphql/__utils__'
import {
  createApplicationMutation,
  updateApplicationMutation,
} from 'ppbl/graphql/mutations'
import { getCreditApplicationDataQuery } from 'ppbl/graphql/queries'
import serverData from 'ppbl/utils/server-data'
import { useApplicationContext } from 'ppbl/hooks'
import clientLogger from 'ppbl/utils/client-logger'
import { trackClick, trackPage } from 'ppbl/utils/fpti-helper'
import { getLocalizedText } from 'ppbl/utils/locale-helper'
import { interpolate, santizeInputFields } from 'ppbl/utils/string-helper'
import { scrollOnErrors } from 'ppbl/utils/validation-helper'
import React, { useEffect, useRef } from 'react'
import { Field, Form, FormSpy } from 'react-final-form'
import {
  scrollToTopOnContinue,
  createApplicationPayloadGenerator,
  getApplicationFieldsData,
  updateApplicationPayloadGeneratorPage1,
  handleGraphqlError,
  clearCampaignDetails,
} from 'ppbl/screens/__utils__'
import {
  applicationPage1Fields,
  applicationPage1FieldsRenewal,
  getApplicationFields,
} from '../applicationFieldMapper'
import './styles.css'
import { cloneAndMask } from 'ppbl/utils/data-masker'
import createDecorator from 'ppbl/utils/form/decorator'

type Props = {
  applicationType: string
  creditApplicationLoading: boolean
}

const fptiData = {
  page: 'applicationPage1',
}

const ApplicationPage1: React.FC<Props> = ({
  applicationType,
  creditApplicationLoading,
}) => {
  const { data, updateApplicationContext } = useApplicationContext()

  useEffect(() => {
    trackPage(
      merge(fptiData, {
        applicationId: data?.applicationMetaData?.applicationId,
      }),
    )
    clientLogger.info('Application Page 1 is loaded')
  }, [])

  let { newApplicationPage1Fields: applicationPageFields } =
    getApplicationFields(applicationType)

  const isRenewalApplication =
    applicationType === 'app-renewal' || applicationType === 'paypal-renewal'
  const renewalToken = serverData.startParams?.renewalToken
  const isPaypalApplication = applicationType === 'paypal'

  const showReadOnlyCard = isRenewalApplication || isPaypalApplication

  const { data: cachedApplicationData } = useQuery(
    getCreditApplicationDataQuery,
  )

  const handleCreateOrUpdateResponse = (response) => {
    clientLogger.info(
      'Response received after creating/updating basic information in application page 1',
      cloneAndMask(response),
    )
    updateApplicationCachedData({ id: response?.id }, cachedApplicationData)
    // push the response.data.credit.businessCredit.createApplication.creditApplication into context
    sessionStorage.setItem('appId', response?.id)
    sessionStorage.setItem('mostRecentCompletedPage', 'applicationPage1')
    updateApplicationContext({
      applicationMetaData: {
        ...data?.applicationMetaData,
        applicationId: response?.id,
        mostRecentCompletedPage: 'applicationPage1',
        legalBusinessName: response?.business?.legalBusinessName,
      },
    })
    navigate(
      `/merchant-lending/loanbuilder/application/us/${applicationType}/applicationPage2`,
    )
  }

  const handleCreateOrUpdateError = (error, callIdentifier) => {
    handleGraphqlError(
      error,
      'Error encountered while creating/updating basic information in application page 1',
      `ApplicationPage 1 - ${callIdentifier}`,
      data?.applicationMetaData?.applicationId,
      renewalToken,
      data?.applicationMetaData?.legalBusinessName,
      applicationType,
    )
  }

  const [createApplication, { loading: createLoading }] = useMutation(
    createApplicationMutation,
    {
      onCompleted(createResponse) {
        handleCreateOrUpdateResponse(
          createResponse?.credit?.businessCredit?.createApplication
            ?.creditApplication,
        )
      },
      onError(createError) {
        handleCreateOrUpdateError(createError, 'createApplicationMutation')
      },
    },
  )
  const [updateApplication, { loading: updateLoading }] = useMutation(
    updateApplicationMutation,
    {
      onCompleted(updateResponse) {
        handleCreateOrUpdateResponse(
          updateResponse?.credit?.businessCredit?.updateApplication
            ?.creditApplication,
        )
      },
      onError(updateError) {
        handleCreateOrUpdateError(updateError, 'updateApplicationMutation')
      },
    },
  )

  const handleApplicationSubmit = async (values: any) => {
    trackClick(
      merge(fptiData, {
        component: 'applicationPage1_continue_button',
        link: 'applicationPage1_continue_button',
        applicationId: data?.applicationMetaData?.applicationId,
      }),
    )
    scrollToTopOnContinue()

    clientLogger.info(
      'application page 1 submitted - Application ID:',
      data?.applicationMetaData?.applicationId,
    )
    if (isPaypalApplication) {
      clientLogger.info(
        'paypal users encrypted account number',
        serverData?.user?.encryptedAccountNumber,
      )
    } else if (renewalToken) {
      clientLogger.info('renewal user renewal token', renewalToken)
    }

    const applicationValues = showReadOnlyCard
      ? getApplicationFieldsData(
          isRenewalApplication
            ? applicationPage1FieldsRenewal
            : applicationPage1Fields,
          cachedApplicationData,
          values,
        )
      : values

    //sanitize the input values to prevent XSS
    santizeInputFields(applicationValues)

    clientLogger.info(
      'Legal bussiness name for the application',
      applicationValues?.legalBusinessName,
    )

    updateApplicationCachedData(values, cachedApplicationData)
    if (data && data?.applicationMetaData?.applicationId) {
      updateApplication({
        variables: {
          updateInput: updateApplicationPayloadGeneratorPage1(
            applicationValues,
            data.applicationMetaData.applicationId,
          ),
        },
      })
    } else {
      createApplication({
        variables: {
          application: createApplicationPayloadGenerator(applicationValues),
        },
      })
    }
    clearCampaignDetails()
  }

  const getCardFields = () => {
    let cardFields = {}

    if (
      applicationType === 'app-renewal' ||
      applicationType === 'paypal-renewal'
    ) {
      cardFields = {
        'Legal business name': cachedApplicationData.legalBusinessName,
        'First name': cachedApplicationData.firstName,
        'Last name': cachedApplicationData.lastName,
        'Email address': cachedApplicationData.emailAddress,
      }
    } else if (applicationType === 'paypal') {
      cardFields = {
        'PayPal account': cachedApplicationData.emailAddress,
      }
    }
    return cardFields
  }

  const checkboxLabel = (
    <CaptionText className="checkboxLabel">
      {interpolate(
        getLocalizedText(
          'application.applicationPages.applicationPage1.checkBoxLabel',
        ),
        {
          termsAndConditions: (
            <Link
              href={getLocalizedText(
                'application.applicationPages.applicationPage1.termsAndConditionsLink',
              )}
              size="sm"
              secondary
            >
              {getLocalizedText(
                'application.applicationPages.applicationPage1.termsAndConditionsLabel',
              )}
            </Link>
          ),
          privacyPolicy: (
            <Link
              href={getLocalizedText(
                'application.applicationPages.applicationPage1.privacyPolicyLink',
              )}
              size="sm"
              secondary
            >
              {getLocalizedText(
                'application.applicationPages.applicationPage1.privacyPolicyLabel',
              )}
            </Link>
          ),
        },
      )}
    </CaptionText>
  )

  const focusOnErrors = createDecorator()

  return (
    <>
      {updateLoading || createLoading || creditApplicationLoading ? (
        <div className="spinner">
          <SpinnerAdapter />
        </div>
      ) : (
        <Form onSubmit={handleApplicationSubmit} decorators={[focusOnErrors]}>
          {({ handleSubmit, hasValidationErrors }) => (
            <div className="applicationFormContainer">
              <form onSubmit={handleSubmit}>
                <div className="applicationTextSection">
                  <Row>
                    <Col form="full">
                      <HeadingText size="sm">
                        {getLocalizedText(
                          'application.applicationPages.applicationPage1.headingText',
                        )}
                      </HeadingText>
                    </Col>
                  </Row>
                  <Row>
                    <Col form="full">
                      <BodyText>
                        {getLocalizedText(
                          'application.applicationPages.applicationPage1.checkEligibilityText',
                        )}
                      </BodyText>
                    </Col>
                  </Row>
                </div>
                {showReadOnlyCard && (
                  <ReadOnlyCard
                    id="applicationPage1Card"
                    fieldsList={getCardFields()}
                    isEditable={false}
                  />
                )}
                <div className="applicationFields">
                  {applicationPageFields.map((applicationField, index) => {
                    if (Array.isArray(applicationField)) {
                      return (
                        <Row key={`applicationFieldsRow${index}`}>
                          {applicationField.map((field) => {
                            const { name, ...props } = field
                            return (
                              <Col key={name} form="half">
                                <Field
                                  name={name}
                                  {...props}
                                  initialValue={
                                    cachedApplicationData?.[name] || null
                                  }
                                />
                              </Col>
                            )
                          })}
                        </Row>
                      )
                    }
                    const { name, ...props } = applicationField
                    const isCheckbox = name === 'agreedToTermsAndConditions'
                    return (
                      <Row key={name}>
                        {isCheckbox ? (
                          <Col className="checkboxGroup" form="full">
                            <Field
                              name={name}
                              {...props}
                              initialValue={
                                cachedApplicationData?.[name] || null
                              }
                              className="checkbox check"
                              label=""
                            />
                            {checkboxLabel}
                          </Col>
                        ) : (
                          <Col form="full">
                            {{
                              doYouHaveAFundingCode: (
                                <FormSpy subscription={{ values: true }}>
                                  {({
                                    values: { howDidYouHearAboutThisProduct },
                                  }) =>
                                    howDidYouHearAboutThisProduct ===
                                      'MAILER' && (
                                      <Field
                                        name={name}
                                        {...props}
                                        initialValue={
                                          cachedApplicationData?.[name] || null
                                        }
                                      />
                                    )
                                  }
                                </FormSpy>
                              ),
                            }[name] || (
                              <Field
                                name={name}
                                {...props}
                                initialValue={
                                  cachedApplicationData?.[name] || null
                                }
                              />
                            )}
                          </Col>
                        )}
                      </Row>
                    )
                  })}
                </div>
                <Button
                  className="ctaSection"
                  size="lg"
                  onClick={() => scrollOnErrors(hasValidationErrors)}
                  type="submit"
                >
                  {getLocalizedText(
                    'application.applicationPages.applicationPage1.continueButtonText',
                  )}
                </Button>
              </form>
            </div>
          )}
        </Form>
      )}
    </>
  )
}

export default ApplicationPage1
